import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, GitMerge, AlertTriangle, FolderTree, X, RefreshCw, Sparkles, Search, ArrowRight, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useDeduplicationStore } from '@/stores/deduplicationStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface Bucket {
  id: string;
  name: string;
  skillCount: number;
}

interface Skill {
  id: string;
  name: string;
  parent_id: string | null;
}

interface MergeSuggestion {
  primaryBucket: string;
  bucketsToMerge: string[];
  reason: string;
}

interface GroupSelection {
  primaryName: string;
  selectedNames: Set<string>;
}

export const BucketDeduplicationPanel = () => {
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState<number | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [newBucketName, setNewBucketName] = useState('');
  const [groupSelections, setGroupSelections] = useState<Record<number, GroupSelection>>({});
  const { toast } = useToast();

  // Use persistent store
  const {
    bucketSuggestions: suggestions,
    bucketScanning: scanning,
    bucketLastScanned,
    setBucketSuggestions: setSuggestions,
    setBucketScanning: setScanning
  } = useDeduplicationStore();

  useEffect(() => {
    fetchData();
  }, []);

  // Initialize selections when suggestions are loaded from store
  useEffect(() => {
    if (suggestions.length > 0 && Object.keys(groupSelections).length === 0) {
      const initialSelections: Record<number, GroupSelection> = {};
      suggestions.forEach((s, idx) => {
        const allNames = [s.primaryBucket, ...s.bucketsToMerge];
        initialSelections[idx] = {
          primaryName: s.primaryBucket,
          selectedNames: new Set(allNames),
        };
      });
      setGroupSelections(initialSelections);
    }
  }, [suggestions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [skillsRes] = await Promise.all([
        supabase.from('skills').select('id, name, parent_id, is_bucket').order('name'),
      ]);

      if (skillsRes.error) throw skillsRes.error;

      const allSkills = skillsRes.data || [];
      const bucketSkills = allSkills.filter(s => s.is_bucket);
      const nonBucketSkills = allSkills.filter(s => !s.is_bucket);

      // Calculate skill count per bucket
      const skillsPerBucket: Record<string, number> = {};
      nonBucketSkills.forEach(skill => {
        if (skill.parent_id) {
          skillsPerBucket[skill.parent_id] = (skillsPerBucket[skill.parent_id] || 0) + 1;
        }
      });

      const bucketsWithCounts = bucketSkills.map(b => ({
        id: b.id,
        name: b.name,
        skillCount: skillsPerBucket[b.id] || 0,
      }));

      setBuckets(bucketsWithCounts);
      setSkills(nonBucketSkills);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Failed to load buckets', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const detectDuplicates = async () => {
    if (buckets.length < 2) {
      toast({ title: 'Need at least 2 buckets to analyze', variant: 'destructive' });
      return;
    }

    setScanning(true);
    setError(null);
    setSuggestions([]);
    setGroupSelections({});

    try {
      const bucketData = buckets.map(b => ({
        name: b.name,
        skillCount: b.skillCount
      }));

      const { data, error: fnError } = await supabase.functions.invoke('suggest-skills', {
        body: {
          buckets: bucketData,
          action: 'suggest_bucket_merges'
        }
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const mergeSuggestions = (data?.mergeSuggestions || []) as MergeSuggestion[];

      // Filter out suggestions where buckets don't exist
      const validSuggestions = mergeSuggestions.filter(s => {
        const allNames = [s.primaryBucket, ...s.bucketsToMerge];
        return allNames.every(name => buckets.some(b => b.name === name));
      });

      setSuggestions(validSuggestions);

      // Initialize selections
      const initialSelections: Record<number, GroupSelection> = {};
      validSuggestions.forEach((s, idx) => {
        const allNames = [s.primaryBucket, ...s.bucketsToMerge];
        initialSelections[idx] = {
          primaryName: s.primaryBucket,
          selectedNames: new Set(allNames),
        };
      });
      setGroupSelections(initialSelections);

      if (validSuggestions.length === 0) {
        toast({ title: 'No similar buckets found' });
      } else {
        toast({ title: `Found ${validSuggestions.length} potential merge(s)` });
      }
    } catch (err: any) {
      console.error('Error detecting duplicates:', err);
      setError(err.message || 'Failed to analyze buckets');
    } finally {
      setScanning(false);
    }
  };

  const toggleBucketSelection = (groupIndex: number, bucketName: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedNames);

      if (newSelected.has(bucketName)) {
        if (bucketName === current.primaryName) {
          toast({ title: 'Cannot deselect primary bucket', variant: 'destructive' });
          return prev;
        }
        newSelected.delete(bucketName);
      } else {
        newSelected.add(bucketName);
      }

      return {
        ...prev,
        [groupIndex]: { ...current, selectedNames: newSelected }
      };
    });
  };

  const setPrimaryBucket = (groupIndex: number, bucketName: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedNames);
      newSelected.add(bucketName);

      return {
        ...prev,
        [groupIndex]: { primaryName: bucketName, selectedNames: newSelected }
      };
    });
  };

  const openMergePreview = (groupIndex: number) => {
    const selection = groupSelections[groupIndex];
    setNewBucketName(selection.primaryName);
    setPreviewIndex(groupIndex);
  };

  const getSkillsForGroup = (groupIndex: number) => {
    const selection = groupSelections[groupIndex];
    if (!selection) return [];

    const selectedBucketIds = buckets
      .filter(b => selection.selectedNames.has(b.name))
      .map(b => b.id);

    return skills.filter(s => s.parent_id && selectedBucketIds.includes(s.parent_id));
  };

  const handleMerge = async () => {
    if (previewIndex === null) return;

    const selection = groupSelections[previewIndex];
    const selectedCount = selection.selectedNames.size;

    if (selectedCount < 2) {
      toast({ title: 'Select at least 2 buckets to merge', variant: 'destructive' });
      return;
    }

    const primaryBucket = buckets.find(b => b.name === selection.primaryName);
    const bucketsToMerge = buckets.filter(
      b => selection.selectedNames.has(b.name) && b.name !== selection.primaryName
    );

    if (!primaryBucket || bucketsToMerge.length === 0) {
      toast({ title: 'Invalid selection', variant: 'destructive' });
      return;
    }

    setMerging(previewIndex);

    try {
      // Rename primary bucket if needed
      if (newBucketName.trim() && newBucketName !== primaryBucket.name) {
        await supabase
          .from('skills')
          .update({ name: newBucketName.trim() })
          .eq('id', primaryBucket.id);
      }

      // Move all skills from merged buckets to primary
      for (const bucket of bucketsToMerge) {
        await supabase
          .from('skills')
          .update({ parent_id: primaryBucket.id })
          .eq('parent_id', bucket.id);

        // Delete the merged bucket
        await supabase.from('skills').delete().eq('id', bucket.id);
      }

      toast({
        title: 'Buckets merged successfully',
        description: `Merged ${bucketsToMerge.length} bucket(s) into "${newBucketName || primaryBucket.name}"`
      });

      setSuggestions(suggestions.filter((_, idx) => idx !== previewIndex));
      setGroupSelections(prev => {
        const updated = { ...prev };
        delete updated[previewIndex];
        return updated;
      });
      setPreviewIndex(null);
      setNewBucketName('');
      fetchData();
    } catch (err: any) {
      console.error('Error merging buckets:', err);
      toast({ title: 'Merge failed', description: err.message, variant: 'destructive' });
    } finally {
      setMerging(null);
    }
  };

  const dismissGroup = async (groupIndex: number) => {
    const group = suggestions[groupIndex];
    const primaryBucket = buckets.find(b => b.name.toLowerCase() === group.primaryBucket.toLowerCase());
    
    if (primaryBucket) {
      // Persist ignored pairs to database
      const ignoreRecords = group.bucketsToMerge.map(bucketName => {
        const dupBucket = buckets.find(b => b.name.toLowerCase() === bucketName.toLowerCase());
        return dupBucket ? {
          entity_type: 'bucket',
          primary_entity_id: primaryBucket.id,
          duplicate_entity_id: dupBucket.id,
          status: 'ignored',
          match_score: 80,
          reason: 'Dismissed by user',
        } : null;
      }).filter(Boolean);

      try {
        if (ignoreRecords.length > 0) {
          await supabase.from('duplicate_suggestions').insert(ignoreRecords);
        }
      } catch (error) {
        console.error('Error saving ignored suggestion:', error);
      }
    }

    setSuggestions(suggestions.filter((_, idx) => idx !== groupIndex));
    setGroupSelections(prev => {
      const updated = { ...prev };
      delete updated[groupIndex];
      return updated;
    });
    toast({ title: 'Dismissed and will not appear again' });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading buckets...</p>
      </div>
    );
  }

  const previewSelection = previewIndex !== null ? groupSelections[previewIndex] : null;
  const previewSkills = previewIndex !== null ? getSkillsForGroup(previewIndex) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FolderTree className="h-5 w-5 text-purple-500" />
            Bucket Deduplication
          </h2>
          <p className="text-sm text-muted-foreground">
            Consolidate similar skill buckets to reduce redundancy
            {bucketLastScanned && (
              <span className="ml-2 text-xs">
                • Last scanned: {new Date(bucketLastScanned).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{buckets.length} buckets</Badge>
          <Button
            onClick={detectDuplicates}
            disabled={scanning || buckets.length < 2}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {scanning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : suggestions.length > 0 ? (
              <RefreshCw className="h-4 w-4" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {scanning ? 'Analyzing...' : suggestions.length > 0 ? 'Rescan' : 'Find Similar Buckets'}
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <p className="text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      {suggestions.length === 0 && !scanning && !error && (
        <Card className="border-muted">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
            <div className="rounded-full bg-muted p-4">
              <Sparkles className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold">AI-Powered Bucket Analysis</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Click "Find Similar Buckets" to analyze your skill buckets for potential consolidation opportunities.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {suggestions.length > 0 && (
        <div className="space-y-4">
          {suggestions.map((suggestion, idx) => {
            const selection = groupSelections[idx];
            if (!selection) return null;

            const allNames = [suggestion.primaryBucket, ...suggestion.bucketsToMerge];
            const selectedCount = selection.selectedNames.size;
            const affectedSkillCount = getSkillsForGroup(idx).length;

            return (
              <Card key={idx} className="overflow-hidden">
                <CardHeader className="bg-purple-500/10 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-purple-500/20 p-2">
                        <FolderTree className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-base">Similar Buckets</CardTitle>
                        <CardDescription>
                          {selectedCount} of {allNames.length} selected • {affectedSkillCount} skills affected
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => dismissGroup(idx)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <p className="text-sm text-muted-foreground">{suggestion.reason}</p>

                  <RadioGroup
                    name={`bucket-group-${idx}`}
                    value={selection.primaryName}
                    onValueChange={(v) => setPrimaryBucket(idx, v)}
                    className="space-y-2"
                  >
                    {allNames.map((name) => {
                      const bucket = buckets.find(b => b.name === name);
                      const isSelected = selection.selectedNames.has(name);
                      const isPrimary = name === selection.primaryName;

                      return (
                        <div
                          key={name}
                          className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                            isPrimary
                              ? 'bg-primary/5 border-primary/30 ring-2 ring-primary/20'
                              : isSelected
                                ? 'bg-muted/30 border-border'
                                : 'border-border/50 bg-muted/10 opacity-60'
                          }`}
                        >
                          <Checkbox
                            id={`check-bucket-${idx}-${name}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleBucketSelection(idx, name)}
                            disabled={isPrimary}
                          />
                          <RadioGroupItem
                            value={name}
                            id={`radio-bucket-${idx}-${name}`}
                            disabled={!isSelected}
                          />
                          <Label
                            htmlFor={`radio-bucket-${idx}-${name}`}
                            className="flex items-center gap-2 cursor-pointer flex-1"
                          >
                            <FolderTree className="h-4 w-4 text-muted-foreground" />
                            {name}
                            {isPrimary && (
                              <Badge variant="default" className="text-xs">Primary</Badge>
                            )}
                            <Badge variant="outline" className="text-xs ml-auto">
                              {bucket?.skillCount || 0} skills
                            </Badge>
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>

                  <Button
                    className="w-full gap-2"
                    onClick={() => openMergePreview(idx)}
                    disabled={selectedCount < 2}
                  >
                    <GitMerge className="h-4 w-4" />
                    Preview & Merge {selectedCount - 1} Bucket(s)
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Merge Preview Dialog */}
      <Dialog open={previewIndex !== null} onOpenChange={(open) => !open && setPreviewIndex(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5" />
              Preview Merge
            </DialogTitle>
            <DialogDescription>
              Review the merge before confirming
            </DialogDescription>
          </DialogHeader>

          {previewSelection && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newBucketName">Merged Bucket Name</Label>
                <Input
                  id="newBucketName"
                  value={newBucketName}
                  onChange={(e) => setNewBucketName(e.target.value)}
                  placeholder="Enter bucket name"
                />
                <p className="text-xs text-muted-foreground">
                  You can rename the bucket after merging
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-muted-foreground">Merging:</span>
                  {[...previewSelection.selectedNames]
                    .filter(n => n !== previewSelection.primaryName)
                    .map((name, i) => (
                      <Badge key={i} variant="outline" className="text-muted-foreground">
                        {name}
                      </Badge>
                    ))}
                  <ArrowRight className="h-4 w-4 text-primary" />
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    {newBucketName || previewSelection.primaryName}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Skills to be consolidated ({previewSkills.length})</Label>
                <ScrollArea className="h-48 border rounded-md p-2">
                  {previewSkills.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {previewSkills.map((skill) => (
                        <Badge key={skill.id} variant="secondary" className="text-xs">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No skills in these buckets
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewIndex(null)}>
              Cancel
            </Button>
            <Button onClick={handleMerge} disabled={merging !== null || !newBucketName.trim()}>
              {merging !== null ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <GitMerge className="h-4 w-4 mr-2" />
                  Confirm Merge
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
